import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Pegar o redirect path se houver
  const redirectTo = location.state?.from || '/';

  // Login form
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Register form
  const [registerForm, setRegisterForm] = useState({
    name: '',
    cpf: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: {
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: ''
    }
  });

  const fetchAddress = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      return;
    }

    setCepLoading(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        alert('CEP não encontrado');
        return;
      }

      setRegisterForm(prev => ({
        ...prev,
        address: {
          ...prev.address,
          cep: cleanCep,
          street: response.data.logradouro,
          neighborhood: response.data.bairro,
          city: response.data.localidade,
          state: response.data.uf
        }
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      alert('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 8) {
      if (value.length > 5) {
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
      }
      setRegisterForm(prev => ({
        ...prev,
        address: { ...prev.address, cep: value }
      }));

      if (value.replace(/\D/g, '').length === 8) {
        fetchAddress(value);
      }
    }
  };

  const formatCPF = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{2})(\d)/, '($1) $2');
      value = value.replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/customer/login`, loginForm);
      
      // Use auth context to persist login
      login(response.data.token, response.data.username);
      
      alert('Login realizado com sucesso!');
      navigate(redirectTo);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validar senhas
    if (registerForm.password !== registerForm.confirmPassword) {
      alert('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (registerForm.password.length < 6) {
      alert('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const registerData = {
        name: registerForm.name,
        cpf: registerForm.cpf.replace(/\D/g, ''),
        phone: registerForm.phone.replace(/\D/g, ''),
        email: registerForm.email,
        password: registerForm.password,
        address: {
          ...registerForm.address,
          cep: registerForm.address.cep.replace(/\D/g, '')
        }
      };

      await axios.post(`${BACKEND_URL}/api/customer/register`, registerData);
      
      // Auto login after registration
      const loginResponse = await axios.post(`${BACKEND_URL}/api/customer/login`, {
        email: registerForm.email,
        password: registerForm.password
      });

      // Use auth context to persist login
      login(loginResponse.data.token, loginResponse.data.username);
      
      alert('Cadastro realizado com sucesso!');
      navigate(redirectTo);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      alert('Por favor, informe seu email');
      return;
    }

    setSendingReset(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/customer/forgot-password`,
        { email: forgotEmail }
      );

      alert('Se o email informado estiver cadastrado, você receberá instruções para recuperação de senha.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      console.error('Erro ao enviar email de recuperação:', error);
      alert('Erro ao enviar email de recuperação');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-md mx-auto px-4 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 hover:text-[#00A86B]"
        >
          <ArrowLeft size={20} className="mr-2" />
          Voltar
        </Button>

        <Card className="p-8">
          {/* Toggle Login/Register */}
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 font-bold rounded-lg transition-colors ${
                isLogin
                  ? 'bg-[#00A86B] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Entrar
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 font-bold rounded-lg transition-colors ${
                !isLogin
                  ? 'bg-[#00A86B] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Cadastrar
            </button>
          </div>

          {isLogin ? (
            // LOGIN FORM
            <form onSubmit={handleLogin} className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Entrar na sua conta</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold mt-6"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-[#00A86B] hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
            </form>
          ) : (
            // REGISTER FORM
            <form onSubmit={handleRegister} className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Criar nova conta</h2>
              
              {/* Personal Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <Input
                  required
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  placeholder="João Silva"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF *
                  </label>
                  <Input
                    required
                    value={registerForm.cpf}
                    onChange={(e) => setRegisterForm({ ...registerForm, cpf: formatCPF(e.target.value) })}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <Input
                    required
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: formatPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  required
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  placeholder="seu@email.com"
                />
              </div>

              {/* Address */}
              <div className="pt-4 border-t">
                <h3 className="font-semibold text-gray-800 mb-3">Endereço</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CEP *
                    </label>
                    <Input
                      required
                      value={registerForm.address.cep}
                      onChange={handleCepChange}
                      placeholder="00000-000"
                      maxLength={9}
                      disabled={cepLoading}
                    />
                    {cepLoading && <p className="text-xs text-gray-500 mt-1">Buscando...</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número *
                    </label>
                    <Input
                      required
                      value={registerForm.address.number}
                      onChange={(e) => setRegisterForm({
                        ...registerForm,
                        address: { ...registerForm.address, number: e.target.value }
                      })}
                      placeholder="123"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rua *
                  </label>
                  <Input
                    required
                    value={registerForm.address.street}
                    onChange={(e) => setRegisterForm({
                      ...registerForm,
                      address: { ...registerForm.address, street: e.target.value }
                    })}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <Input
                    value={registerForm.address.complement}
                    onChange={(e) => setRegisterForm({
                      ...registerForm,
                      address: { ...registerForm.address, complement: e.target.value }
                    })}
                    placeholder="Apto, bloco, etc"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bairro *
                    </label>
                    <Input
                      required
                      value={registerForm.address.neighborhood}
                      onChange={(e) => setRegisterForm({
                        ...registerForm,
                        address: { ...registerForm.address, neighborhood: e.target.value }
                      })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cidade *
                    </label>
                    <Input
                      required
                      value={registerForm.address.city}
                      onChange={(e) => setRegisterForm({
                        ...registerForm,
                        address: { ...registerForm.address, city: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <Input
                    required
                    value={registerForm.address.state}
                    onChange={(e) => setRegisterForm({
                      ...registerForm,
                      address: { ...registerForm.address, state: e.target.value.toUpperCase() }
                    })}
                    maxLength={2}
                    placeholder="SP"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="pt-4 border-t">
                <h3 className="font-semibold text-gray-800 mb-3">Senha</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha * (mínimo 6 caracteres)
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      placeholder="Digite sua senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar Senha *
                  </label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                    placeholder="Confirme sua senha"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold mt-6"
              >
                {loading ? 'Cadastrando...' : 'Criar Conta'}
              </Button>
            </form>
          )}
        </Card>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Recuperar Senha</h2>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotEmail('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Digite seu email cadastrado. Você receberá um link para redefinir sua senha.
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="seu@email.com"
                  className="w-full"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmail('');
                  }}
                  className="flex-1"
                  disabled={sendingReset}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={sendingReset}
                  className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
                >
                  {sendingReset ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomerAuth;
